import { defineComponent as _defineComponent } from 'vue'
import { normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = ["id"]

import { ref, watch, onMounted, PropType, Ref } from 'vue'
import * as echarts from 'echarts/lib/echarts'
import 'echarts/lib/chart/line'
import 'echarts/lib/component/grid'
import 'echarts/lib/component/tooltip'
import 'echarts/lib/component/title'
import useEchartResize from '@/hooks/useEchartResize'
import useI18nTl from '@/hooks/useI18nTl'
import { isUndefined } from 'lodash'
import Moment from 'moment'
import { dateFormatRFC3339 } from '@/common/tools'


import { defineComponent, shallowRef } from 'vue'
import { number } from 'echarts'

const __default__ = defineComponent({
  name: 'TrendChart',
})

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
  chartId: {
    type: String,
    required: true,
  },
  yTitle: {
    type: Array as PropType<Array<string>>,
    default: () => [''],
  },
  chartColors: {
    type: Array as PropType<Array<string>>,
    default: () => [],
  },
  axisColor: {
    type: Object,
    default: () => ({
      colorAxisLine: '#757575',
      colorAxisLabel: '#757575',
    }),
  },

  chartData: {
    type: Array as PropType<Array<{ xData: Array<number>; yData: Array<number> }>>,
    default: () => [
      {
        xData: [],
        yData: [],
      },
    ],
  },
  height: {
    type: String,
    default: '320px',
  },
  isInstantaneousValue: {
    type: Boolean,
  },
  unitTextKey: {
    type: String,
  },
  format: {
    type: String,
    default: () => 'YYYY/MM/DD HH:mm',
  },
},
  setup(__props) {

const props = __props



const seriesConfig: Ref<Array<any>> = ref([])
const chart: Ref<undefined | any> = shallowRef(undefined)
const { addListener } = useEchartResize()

watch(
  () => props.chartData,
  () => {
    drawChart()
  },
)

onMounted(() => {
  drawChart()
})

const NO_DATA_VALUE = -1
let noYAxisDataMap: Record<string, Array<number>> = {}

const storeNoDataXAxis = (yData: Array<number>, xData: Array<number>, title: string) => {
  yData.forEach((item, index) => {
    if (item === NO_DATA_VALUE) {
      if (!noYAxisDataMap[title]) {
        noYAxisDataMap[title] = []
      }
      noYAxisDataMap[title].push(xData[index])
    }
  })
}

const handleYAxisData = (arr: Array<number>) =>
  arr.map((item) => (item === NO_DATA_VALUE ? 0 : item))

const setSeriesConfig = () => {
  seriesConfig.value = []
  noYAxisDataMap = {}

  // console.log("--->props.chartDatas: ", props.yTitle.length, props.chartData)
  for (let i = 0; i < props.yTitle.length; i += 1) {
    // console.log("--->chartData: ", props.yTitle[i], props.chartData[i])

    let areaColor = i % 6 === 0 && i !== 0 ? props.chartColors[6] : props.chartColors[i % 6]
    storeNoDataXAxis(props.chartData[i].yData, props.chartData[i].xData, props.yTitle[i])
    seriesConfig.value.push({
      name: props.yTitle[i],
      type: 'line',
      smooth: true,
      symbolSize: 5,
      showSymbol: false,
      data: handleYAxisData(props.chartData[i].yData),
      step: false,
      lineStyle: {
        width: 2,
      },
      areaStyle: {
        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          {
            offset: 0,
            color: areaColor,
          },
          {
            offset: 1,
            color: `${areaColor}00`,
          },
        ]),
        opacity: 0.2,
      },
    })
  }
}

const _formatTime = (time: string) => {
  return Moment(parseInt(time)).format(props.format)
}

const createTooltip = (xAxis: string, title: string, color: string, val?: number) => {
  const container = document.createElement('div')
  container.innerHTML = `
  <div class="polyline-chart-tooltip">
    <p class="x-value">${_formatTime(xAxis)}</p>
    <div class="tooltip-body">
      <div>
        <i class="badge" style="background-color:${color}"></i>
        <span>${title}</span>
      </div>
      ${!isUndefined(val) ? `<p class="num">${val}</p>` : ''}
    </div>
  </div>
  `
  return container
}

const { t } = useI18nTl('Dashboard')
const getInterval = (index: number) => {
  if (props.format.includes('HH')) {
    return '1 Hours'
  } else if (props.format.includes('DD')) {
    return '1 Days'
  } else if (props.format.includes('MM')) {
    return '1 Months'
  }

  // const xData: Array<number> = props.chartData?.[0]?.xData || []
  // if (index === 0 && !isUndefined(xData[0]) && !isUndefined(xData[1])) {
  //   return xData[1] - xData[0]
  // }
  // if (!isUndefined(xData[index]) && !isUndefined(xData[index - 1])) {
  //   return xData[index] - xData[index - 1]
  // }
  return undefined
}
const drawChart = () => {
  setSeriesConfig()
  let Dom = document.getElementById(props.chartId)

  if (!chart.value) {
    chart.value = echarts.init(Dom)
    addListener(chart.value)
  }
  const option = {
    color: props.chartColors,
    grid: {
      top: '5%',
      right: 30,
      bottom: '3%',
      left: 24,
      containLabel: true,
    },
    tooltip: {
      trigger: 'axis',
      confine: true,
      formatter: (params: Array<any>) => {
        if (!params[0]) {
          return ''
        }
        const { axisValue, color, seriesName, value, dataIndex } = params[0]
        let valueShowInTooltip = value
        const isNoData = noYAxisDataMap[seriesName]?.includes(axisValue)
        if (isNoData) {
          valueShowInTooltip = 'NaN'
        }
        if (!props.unitTextKey) {
          return createTooltip(axisValue, seriesName, color, valueShowInTooltip)
        }
        let title = ''
        const interval = getInterval(dataIndex)
        if (!props.isInstantaneousValue && interval) {
          title = t(props.unitTextKey, { interval: interval, n: value })
        } else {
          title = isNoData ? t(props.unitTextKey, { n: 0 }) : t(props.unitTextKey, { n: value })
        }
        return createTooltip(axisValue, title, color)
      },
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: props.chartData[0].xData,
      axisLine: {
        lineStyle: {
          color: props.axisColor.colorAxisLine,
        },
      },
      axisLabel: {
        hideOverlap: true,
        showMinLabel: true,
        color: props.axisColor.colorAxisLabel,
        formatter(value: string) {
          return `${_formatTime(value)}`
        },
      },
    },
    yAxis: {
      type: 'value',
      axisLine: {
        lineStyle: {
          color: props.axisColor.colorAxisLine,
        },
      },
      splitLine: {
        show: false,
      },
      axisLabel: {
        color: props.axisColor.colorAxisLabel,
      },
      minInterval: 1,
    },
    series: seriesConfig.value,
  }
  chart.value?.setOption(option)
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    id: __props.chartId,
    style: _normalizeStyle({ height: __props.height, width: '100%' })
  }, null, 12, _hoisted_1))
}
}

})